import { useState, useEffect, useMemo } from "react";
import {
	CircularProgress,
	Box,
	Typography,
	TextField,
	Tooltip,
	FormHelperText,
	Grid,
	Stack,
	Skeleton,
	IconButton,
	InputAdornment,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { isAfter, addYears, addMonths, addDays, parse, isValid } from "date-fns";
import {
	MIN_POLICY_PERIOD,
	MAX_POLICY_PERIOD,
	MAX_FORWARD_DAYS,
	isDateDisabled,
	MAX_DATE,
	MIN_DATE,
} from "../../helper/constants";
import { colours } from "../../shared/colors";
import { useQuoteContext } from "../../shared/contexts/QuoteContext";
import { useDispatch } from "react-redux";
import { clearError } from "../../services/features/errorSlice/errorSlice";

const LoadingCircle = () => (
	<Box justifyContent="center">
		<CircularProgress size="1em" sx={{ color: colours.primary.main }} />
	</Box>
);

/**
 * Component for Policy Period Date Pickers.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.options - Formik form options.
 *
 * @returns {JSX.Element} The JSX element for the policy period date pickers.
 */
export const PolicyPeriodDatePickers = (props: any) => {
	const dispatch = useDispatch();

	const formik = props.options;

	const { isQuoteBound, isDeclined, isReferred, isReferralApproved, minDate, maxDate, minPolicyPeriod, maxPolicyPeriod } = useQuoteContext();

	const { loadingProducts, setLoadingProducts } = useQuoteContext();

	const changeStartDateField = (newDate: Date | null) => {
		formik.setFieldValue("startDate", newDate);
		// Update end date to be 12 months from startDate
		if (newDate && isValid(newDate) && !isDateDisabled(newDate, maxDate, minDate)) {
			const newEndDate = addMonths(newDate, 12);
			changeEndDateField(newEndDate);
		}
	};

	const changeStartDate = (newDate: Date | null) => {
		if (isValid(newDate)) {
			props.setSelectedStartDate(newDate);
			if (newDate && !isDateDisabled(newDate, maxDate, minDate)) {
				dispatch(clearError());
				const newEndDate = addMonths(newDate, 12);
				changeEndDate(newEndDate);
			}
		}
	};

	const changeEndDateField = (newDate: Date | null) => {
		formik.setFieldValue("endDate", newDate);
	};

	const changeEndDate = (newDate: Date | null) => {
		if (isValid(newDate) && !isReferralApproved) {
			props.setSelectedEndDate(newDate);
			const minimumDate = addMonths(props.selectedStartDate ?? new Date(), minPolicyPeriod);
			const maximumDate = addMonths(props.selectedStartDate ?? new Date(), maxPolicyPeriod);
			if (!isDateDisabled(newDate, maximumDate, minimumDate)) {
				dispatch(clearError());
			}
		}
	};

	const readonly = isQuoteBound || isReferred || isDeclined;

	return (
		<Box pt="1em" alignItems="center">
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<Grid container spacing={1}>
					<Grid item xs={12} sm={6}>
						<DesktopDatePicker
							disabled={loadingProducts || readonly}
							label="Start date"
							format="dd/MM/yyyy"
							value={props.selectedStartDate}
							onAccept={changeStartDate}
							onChange={changeStartDateField}
							slotProps={{
								textField: {
									onBlur: () => changeStartDate(formik.values.startDate),
									name: "startDate",
									error:
										!readonly &&
										formik.touched.startDate &&
										Boolean(formik.errors.startDate),
									// helperText:
									// 	!readonly && !quoteGroup && (formik.touched.startDate &&
									// 	formik.errors.startDate),
								},
							}}
							slots={{
								...(loadingProducts &&
									!readonly && {
										inputAdornment: (props) => (
											<InputAdornment position="end">
												<LoadingCircle />
											</InputAdornment>
										),
									}),
							}}
							//   slotProps={{ textField: { onBlur: () => changeStartDate(selectedStartDateField) } }}
							disablePast
							minDate={minDate}
							maxDate={maxDate}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<DesktopDatePicker
							disabled={loadingProducts || readonly || isReferralApproved}
							label="End date"
							format="dd/MM/yyyy"
							value={props.selectedEndDate}
							onAccept={changeEndDate}
							onChange={changeEndDateField}
							slotProps={{
								textField: {
									onBlur: () => changeEndDate(formik.values.endDate),
									error:
										!readonly &&
										formik.touched.endDate &&
										Boolean(formik.errors.endDate),
									// helperText:
									// 	!readonly && !quoteGroup && (formik.touched.endDate &&
									// 	formik.errors.endDate),
								},
							}}
							slots={{
								...(loadingProducts &&
									!readonly && {
										inputAdornment: (props) => (
											<InputAdornment position="end">
												<LoadingCircle />
											</InputAdornment>
										),
									}),
							}}
							//   slotProps={{ textField: { onBlur: () => changeEndDate(selectedEndDateField) } }}
							minDate={addMonths(
								props.selectedStartDate ?? new Date(),
								minPolicyPeriod
							)}
							maxDate={addMonths(
								props.selectedStartDate ?? new Date(),
								maxPolicyPeriod
							)}
							disablePast
							disableFuture={false}
						/>
					</Grid>
				</Grid>
			</LocalizationProvider>
		</Box>
	);
};
